import React, { useContext, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import * as Yup from 'yup';
import { FormChanges } from 'utils';
import { UserContext } from 'context';
import { Field, Formik } from 'formik';
import { FETCH_STATE } from 'constants';
import List from '@material-ui/core/List';
import { BaseModal } from 'components/UI';
import ListItem from '@material-ui/core/ListItem';
import Typography from '@material-ui/core/Typography';
import { Box, Grid, Tooltip, makeStyles } from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import ListItemText from '@material-ui/core/ListItemText';
import { TextField, SwitchField } from 'components/FormFields';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { PartnersSelect, TimezoneSelect, VehiclesGroupsSelect } from 'components/AsyncFields';

const UpdateVehicleGroupModal = ({ title, vehicleGroup, open, handleClick }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { orgType } = useContext(UserContext);
  const settings = useSelector((state) => state.config?.settings);
  const hasIncludeAnonymousEnabled = settings?.core?.reservation?.anonymous_search || false;
  const { fetchCreateVehicleGroupSuccess, errors } = useSelector((state) => state.vehiclesGroups);
  const fetchUpdateVehicleGroupSuccess = useSelector(
    (state) => state.vehiclesGroups.fetchUpdateVehicleGroupSuccess
  );

  const initialValues = {
    name: vehicleGroup?.name || '',
    label: vehicleGroup?.label || '',
    time_zone: vehicleGroup?.time_zone || '',
    parent_id: vehicleGroup?.parent?.id || '',
    driver_access: vehicleGroup?.driver_access || false,
    include_anonymous: vehicleGroup?.include_anonymous || false,
    rules_set_id: vehicleGroup?.rules_set_id || undefined,
    organization_id: vehicleGroup?.parent?.organization_id || '',
  };

  const formSchema = Yup.object().shape({
    name: Yup.string().required(i18n.t('common.required_field')),
    label:
      orgType === 'msServiceOrg'
        ? Yup.string().required(i18n.t('common.required_field'))
        : Yup.string(),
    time_zone: Yup.string().required(i18n.t('common.required_field')),
    organization_id:
      orgType === 'msServiceOrg'
        ? Yup.number().required(i18n.t('common.required_field'))
        : Yup.number(),
    parent_id:
      orgType === 'msServiceOrg'
        ? Yup.number().required(i18n.t('common.required_field'))
        : Yup.number(),
    driver_access: Yup.boolean(),
    include_anonymous: Yup.boolean(),
  });

  const submit = (values) => {
    let payload = {};
    let filterPayload =
      orgType === 'msOwnerOrg'
        ? {
            name: values?.name,
            time_zone: values?.time_zone,
          }
        : { ...values };

    // remove empty fields
    Object.keys(filterPayload).forEach(
      (key) => !filterPayload[key] && filterPayload[key] !== false && (filterPayload[key] = null)
    );

    if (!filterPayload.rules_set_id) delete filterPayload.rules_set_id;

    if (vehicleGroup?.id) {
      payload = { ...filterPayload };
    } else {
      payload = {
        ...filterPayload,
        group_type: 'CUSTOMER',
      };
    }

    delete payload?.organization_id;
    dispatch(VehiclesGroupsActions.fetchUpdateVehiclesGroups(vehicleGroup?.id, payload));
  };

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  useEffect(() => {
    if (vehicleGroup?.id && fetchCreateVehicleGroupSuccess) {
      dispatch(VehiclesGroupsActions.fetchVehicleGroupById(vehicleGroup?.id, null));
      dispatch(VehiclesGroupsActions.resetOnboarding());
      handleClick();
    }
  }, [dispatch, handleClick, vehicleGroup, fetchCreateVehicleGroupSuccess]);

  useEffect(() => {
    if (fetchUpdateVehicleGroupSuccess === FETCH_STATE.DONE) {
      handleClick();
    }
  }, [fetchUpdateVehicleGroupSuccess]);

  return (
    <BaseModal
      size="md"
      open={open}
      maxWidth={745}
      loading={false}
      hasEnterButton
      hasCancelButton
      onEnter={handleSubmit}
      onCancel={handleClick}
      enableCloseButton={false}
      enterButtonLabel={i18n.t('common.update')}
      title={
        title ||
        (orgType === 'msOwnerOrg'
          ? i18n.t('vehicle_groups.update_service_area')
          : i18n.t('vehicle_groups.update_vehicle_group'))
      }
    >
      <Grid container direction="row" justifyContent="center" alignItems="center">
        <Grid item xs={12}>
          <Formik
            enableReinitialize
            innerRef={formRef}
            initialValues={initialValues}
            validationSchema={formSchema}
            onSubmit={submit}
          >
            {({ handleSubmit, values, setFieldValue }) => (
              <form noValidate onSubmit={handleSubmit}>
                <Grid
                  container
                  spacing={2}
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                >
                  <Grid item xs={12} sm={6} md={6}>
                    <Field
                      type="text"
                      name="name"
                      component={TextField}
                      asyncErrors={errors?.name}
                      label={i18n.t('customer_groups.internal_name')}
                    />
                  </Grid>
                  {(orgType === 'msServiceOrg' || orgType === 'plainOrg') && (
                    <>
                      <Grid item xs={12} sm={6} md={6}>
                        <Field
                          type="text"
                          name="label"
                          component={TextField}
                          asyncErrors={errors?.label}
                          label={i18n.t('customer_groups.customer_label')}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <Field
                          name="organization_id"
                          component={PartnersSelect}
                          label={i18n.t('customer_groups.fleet_owner')}
                          onChange={(value) => {
                            if (value?.id !== values?.organization_id) {
                              setFieldValue('parent_id', '');
                            }
                          }}
                          searchBy="keyword"
                          firstLoad={!!values?.organization_id}
                          customParams={{ status: ['ACTIVE', 'CANREQUESTED'] }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6}>
                        <Field
                          name="parent_id"
                          component={VehiclesGroupsSelect}
                          disabled={!values.organization_id}
                          customParams={{
                            organization_id: values.organization_id,
                          }}
                          label={i18n.t('customer_groups.fleet')}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12} sm={6} md={6}>
                    <Field
                      name="time_zone"
                      component={TimezoneSelect}
                      label={i18n.t('customer_groups.timezone')}
                    />
                  </Grid>
                  {(orgType === 'msServiceOrg' || orgType === 'plainOrg') && (
                    <>
                      <Grid item xs={12} sm={12} md={12}>
                        <Box mx={3}>
                          <Box display="flex" alignItems="center" justifyContent="flex-start">
                            <Field
                              name="driver_access"
                              color="primary"
                              component={SwitchField}
                              onChange={(value) => {
                                setFieldValue('driver_access', value);
                                if (!value) {
                                  setFieldValue('include_anonymous', value);
                                }
                              }}
                            />
                            <Typography variant="h6">
                              <Box
                                ml={2}
                                fontWeight="fontWeightMedium"
                                fontSize={14}
                                style={{ height: '100%' }}
                              >
                                {i18n.t('vehicle.group_activation')}
                              </Box>
                            </Typography>
                            <Tooltip
                              title={i18n.t('vehicle.group_activation_tooltip')}
                              placement="bottom-end"
                            >
                              <InfoIcon color="primary" fontSize="small" />
                            </Tooltip>
                          </Box>
                          {hasIncludeAnonymousEnabled && (
                            <>
                              <Box display="flex" alignItems="center" justifyContent="flex-start">
                                <Field
                                  name="include_anonymous"
                                  color="primary"
                                  component={SwitchField}
                                  onChange={(value) => {
                                    setFieldValue('include_anonymous', value);
                                    if (value) {
                                      setFieldValue('driver_access', value);
                                    }
                                  }}
                                />
                                <Typography variant="h6">
                                  <Box
                                    ml={2}
                                    fontWeight="fontWeightMedium"
                                    fontSize={14}
                                    style={{ height: '100%' }}
                                  >
                                    {i18n.t('vehicle.include_anonymous')}
                                  </Box>
                                </Typography>
                                <Tooltip
                                  title={i18n.t('vehicle.include_anonymous_tooltip')}
                                  placement="bottom-end"
                                >
                                  <InfoIcon color="primary" fontSize="small" />
                                </Tooltip>
                              </Box>
                            </>
                          )}
                        </Box>
                      </Grid>
                      {values?.driver_access &&
                        vehicleGroup?.vehicle_association_requirements?.length > 0 && (
                          <Grid item xs={12} sm={12} md={12}>
                            <Typography variant="h6">
                              <Box
                                fontWeight="fontWeightMedium"
                                fontSize={14}
                                style={{ height: '100%' }}
                              >
                                {i18n.t('common.missing')}
                              </Box>
                            </Typography>
                            <List dense={true}>
                              {vehicleGroup?.vehicle_association_requirements?.map(
                                (value, index) => (
                                  <ListItem key={index}>
                                    <ListItemText primary={value} />
                                  </ListItem>
                                )
                              )}
                            </List>
                          </Grid>
                        )}
                    </>
                  )}
                </Grid>
                <FormChanges initialValues={initialValues} onSave={() => {}} />
              </form>
            )}
          </Formik>
        </Grid>
      </Grid>
    </BaseModal>
  );
};

UpdateVehicleGroupModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

UpdateVehicleGroupModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default UpdateVehicleGroupModal;
